import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormGroup,
  ControlContainer,
  FormGroupDirective,
  FormControl,
  Validators,
  AsyncValidatorFn,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { Regex } from '@shared/enums/regex';
import { DealerClaimType } from '@core/enums/dealer-claim-type';
import { DuplicateImeiInShoppingCartValidator, ImeiValidator } from '@sales/validators/imei-validator';
import { SimService } from '@sales/services/sim.service';
import { SimCardValidator } from '@sales/validators/async-sim-card-validator';
import { SimCardType } from '@sales/models/simcard.model';
import { IconNames } from '@shared/enums/icon-names';
import { FormStatus } from '@sales/models/form-status.enum';
import { ErrorStateOnTouchedOrInvalidValue } from '@app/material/ErrorStateOnTouchedOrInvalidValue';
import { MbbProduct } from '@sales/modules/mbb/models/mbb-product.model';
import { Store } from '@ngxs/store';
import { RepeatedMbbIccValidator } from '@sales/validators/repeated-mbb-icc-validator';
import { FormValidationService } from '@app/shared/services/form-validation.service';
import { ShoppingCartSelectors } from '@app/sales/states/shopping-cart/shopping-cart.selectors';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { AsyncPipe } from '@angular/common';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';

@Component({
  selector: 'app-mbb-router-details',
  templateUrl: './mbb-router-details.component.html',
  styleUrls: ['./mbb-router-details.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [FormsModule, ReactiveFormsModule, IceInputFieldComponent, SpinnerComponent, AsyncPipe],
})
export class MbbRouterDetailsComponent implements OnInit, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);
  private simService = inject(SimService);
  private store = inject(Store);

  DealerClaimType = DealerClaimType;
  IconNames = IconNames;
  FormStatus = FormStatus;
  SimCardType = SimCardType;
  getImeiErrorText = FormValidationService.getImeiErrorText;
  getSimCardErrorTexts = FormValidationService.getSimCardErrorTexts;

  @Input() mbbProduct: MbbProduct;
  @Input() mbbRouterDetailsForm: FormGroup;

  @Output() mbbRouterDetailsImeiChanged: EventEmitter<string> = new EventEmitter();
  @Output() mbbRouterDetailsIccChanged: EventEmitter<string> = new EventEmitter();
  @Output() isInvalid = new EventEmitter();

  get hasDuplicateImeiError(): boolean {
    return this.form.controls.imei.hasError('imeiDuplicateInvalid');
  }

  form: FormGroup;
  invalidValueOrTouched = new ErrorStateOnTouchedOrInvalidValue();
  subscriptions$: Subscription[] = [];

  public static buildMbbRouterDetailsForm(
    productId: string,
    imei: string,
    icc: string,
    iccValidator: AsyncValidatorFn,
    store: Store
  ): FormGroup {
    let form = new FormGroup({
      imei: new FormControl(imei, [
        Validators.required,
        Validators.minLength(15),
        Validators.maxLength(16),
        Validators.pattern(Regex.IMEI),
        ImeiValidator(),
      ]),
      icc: new FormControl(
        icc,
        [
          Validators.required,
          Validators.pattern(Regex.ICC),
          Validators.minLength(19),
          Validators.maxLength(20),
          RepeatedMbbIccValidator(productId, store),
        ],
        [iccValidator]
      ),
    });

    if (store.selectSnapshot<boolean>(DealerStateSelectors.canUpdateImei)) {
      // We want to ignore imei duplication check for the relevant imei when editing an item in the shopping cart.
      const imeisInShoppingCart = store.selectSnapshot<string[]>(ShoppingCartSelectors.imeisInShoppingCart);
      const indexOfExistingImei = imeisInShoppingCart.findIndex(imeiInCart => imeiInCart === imei);
      const otherImeisInShoppingCart = imeisInShoppingCart.filter(imeiInCart => imeiInCart !== imei);

      form.controls.imei.addValidators([
        DuplicateImeiInShoppingCartValidator(indexOfExistingImei === -1 ? imeisInShoppingCart : otherImeisInShoppingCart),
      ]);
    }

    return form;
  }

  ngOnInit() {
    this.form = this.buildForm(this.mbbProduct);

    this.subscriptions$.push(
      this.form.valueChanges
        .pipe(
          debounceTime(100),
          tap(() => this.isInvalid.emit(this.form.invalid))
        )
        .subscribe()
    );

    this.subscriptions$.push(
      this.imeiField.valueChanges.pipe(tap(newValue => this.mbbRouterDetailsImeiChanged.emit(newValue))).subscribe()
    );
    this.subscriptions$.push(
      this.iccField.valueChanges.pipe(tap(newValue => this.mbbRouterDetailsIccChanged.emit(newValue))).subscribe()
    );

    this.isInvalid.emit(this.form.invalid);

    if (this.form.invalid && this.mbbProduct.mbbRouter.name) {
      this.imeiField.markAsTouched();
      this.iccField.markAsTouched();
    }
  }

  private buildForm(mbbProduct: MbbProduct): FormGroup {
    if (this.formGroupDirective.form.get('mbbRouterDetails')) {
      this.formGroupDirective.form.removeControl('mbbRouterDetails');
    }
    this.formGroupDirective.form.addControl(
      'mbbRouterDetails',
      MbbRouterDetailsComponent.buildMbbRouterDetailsForm(
        mbbProduct.id,
        mbbProduct.imei,
        mbbProduct.icc,
        SimCardValidator(this.simService, SimCardType.Mbb, () => {
          this.formGroupDirective.form.controls.mbbRouterDetails.updateValueAndValidity();
          this.isInvalid.emit(this.formGroupDirective.form.invalid);
        }),
        this.store
      )
    );
    return this.formGroupDirective.form.get('mbbRouterDetails') as FormGroup;
  }

  get imeiField() {
    return this.form.controls.imei;
  }

  get iccField() {
    return this.form.controls.icc;
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
